import { ChangeEvent, ReactNode } from 'react';
import { ColumnKey, ColumnType, GridColumn } from '../types/alternative-calculation.types';
import { CaseCalculationAlternativePart, CaseCalculationPart } from '../../../interfaces/Case';

export const GRID_COLUMNS: GridColumn[] = [
  {
    key: ColumnKey.Code,
    translationKey: 'CASE_CALCULATION_ALTERNATIVE_CALCULATION_DETAILS_CODE_LABEL',
    label: 'Cod',
    className: 'cCod',
    type: ColumnType.Text,
    numeric: false
  },
  {
    key: ColumnKey.Name,
    translationKey: 'ORDER_PART_DETAILS_NAME_LABEL',
    label: 'Denumire',
    className: 'cDen',
    type: ColumnType.Text,
    numeric: false
  },
  {
    key: ColumnKey.NetPrice,
    translationKey: 'CALCULATION_DETAILS_NET_PRICE_LKQ_HEADER',
    label: 'Pret Net',
    className: 'cNet',
    type: ColumnType.Range,
    numeric: true
  },
  {
    key: ColumnKey.Price,
    translationKey: 'CASE_CALCULATION_ALTERNATIVE_CALCULATION_DETAILS_PRICE_LABEL',
    label: 'Pret',
    className: 'cPret',
    type: ColumnType.Range,
    numeric: true
  },
  {
    key: ColumnKey.Quality,
    translationKey: 'CASE_CALCULATION_ALTERNATIVE_CALCULATION_DETAILS_QUALITY_LABEL',
    label: 'Calitate',
    className: 'cCal',
    type: ColumnType.Select,
    numeric: false
  },
  {
    key: ColumnKey.Delivery,
    translationKey: 'CASE_CALCULATION_ALTERNATIVE_CALCULATION_DETAILS_DELIVERY_LABEL',
    label: 'Livrare',
    className: 'cLiv',
    type: ColumnType.Date,
    numeric: false
  },
  {
    key: ColumnKey.Producer,
    translationKey: 'CASE_CALCULATION_ALTERNATIVE_CALCULATION_DETAILS_OWNER_LABEL_ACRONYM',
    label: 'Producator',
    className: 'cProd',
    type: ColumnType.Select,
    numeric: false
  },
  {
    key: ColumnKey.Supplier,
    translationKey: 'CASE_CALCULATION_ALTERNATIVE_CALCULATION_DETAILS_SUPLIER_LABEL',
    label: 'Furnizor',
    className: 'cFur',
    type: ColumnType.Select,
    numeric: false
  },
  {
    key: ColumnKey.Quantity,
    translationKey: 'CASE_CALCULATION_ALTERNATIVE_CALCULATION_DETAILS_QUANTITY_INFO_LABEL',
    label: 'Cant.',
    className: 'cCant',
    type: ColumnType.Range,
    numeric: true
  },
  {
    key: ColumnKey.Location,
    translationKey: 'CASE_CALCULATION_ALTERNATIVE_CALCULATION_DETAILS_STOCK_LOCATION_LABEL',
    label: 'Locatie',
    className: 'cLoc',
    // No filter on Location (type null → header renders no filter control for it).
    type: null,
    numeric: false
  }
];

export const COLUMN_WIDTH: Record<string, string> = {
  [ColumnKey.Code]: '136px',
  [ColumnKey.Name]: 'minmax(120px,1fr)',
  [ColumnKey.NetPrice]: '96px',
  [ColumnKey.Price]: '104px',
  [ColumnKey.Quality]: '72px',
  [ColumnKey.Delivery]: '232px',
  [ColumnKey.Producer]: '180px',
  [ColumnKey.Supplier]: '112px',
  [ColumnKey.Quantity]: '62px',
  [ColumnKey.Location]: '160px',
  // Trailing column: action buttons on the part row, difference value on the alternative rows.
  actions: '150px'
};
const leadingTracks = (variantsEnabled: boolean): string[] => ['38px', variantsEnabled ? '52px' : '36px'];
const TRAILING_TRACK = COLUMN_WIDTH.actions;

export const buildGridTemplate = (showNetPrice: boolean, variantsEnabled: boolean): string => {
  const columnTracks = GRID_COLUMNS.filter(
    (col) =>
      (showNetPrice || col.key !== ColumnKey.NetPrice) &&
      (variantsEnabled || col.key !== ColumnKey.Location)
  ).map((col) => COLUMN_WIDTH[col.key]);
  return [...leadingTracks(variantsEnabled), ...columnTracks, TRAILING_TRACK].join(' ');
};

export interface GridCellCtx {
  part: CaseCalculationPart;
  alternativePart: CaseCalculationAlternativePart | null;
  onPriceChange: (e: ChangeEvent<HTMLInputElement>) => void;
  onQuantityChange: (e: ChangeEvent<HTMLInputElement>) => void;
  onQuery: () => void;
  onOpenNotify: () => void;
  onAddAlternativePart: () => void;
  onDeletePart: () => void;
  infoTooltip: ReactNode;
}

export interface GridColumnConfig {
  key: string;
  width: string;
  renderHeaderCell: () => ReactNode;
  renderBodyCell: (ctx: GridCellCtx) => ReactNode;
  sortKey: ColumnKey | null;
  filterable: boolean;
  numeric: boolean;
}

export const buildGridTemplateFrom = (
  cols: GridColumnConfig[],
  leading: string[] = leadingTracks(true)
): string => [...leading, ...cols.map((col) => col.width)].join(' ');
